<template>
  <ag-grid-vue
    v-bind="{ ...defaultProps, ...$attrs }"
    v-bind:column-defs="effectiveColumnDefs"
    class="ag-theme-alpine data-grid"
    v-on="$listeners"
    v-on:grid-ready="onGridReady"
    v-on:selection-changed="onSelectionChanged"
  />
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import * as GridRenderers from '@/components/gridRenderer'

export default {
  name: 'AppDataGrid',
  components: {
    AgGridVue,
    ...GridRenderers,
  },
  inheritAttrs: false,
  props: {
    /**
     * カラム定義
     */
    columnDefs: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択値
     * @model
     */
    value: {
      type: Array,
      default: null,
    },
    /**
     * 選択値として使用するデータのフィールド名
     */
    valueField: {
      type: String,
      default: 'id',
    },
    /**
     * v-modelでオブジェクトの配列を返す場合はtrue
     * falseの場合はvalueFieldで指定された値の配列を返す
     */
    returnObject: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    /**
     * gridApi
     * @type {Ref.<GridApi>}
     */
    const gridApi = ref(null)
    /**
     * カラム系のイベントを全てlistenして1つのイベントにまとめるショートハンド
     * @see https://stackoverflow.com/questions/54823776/is-there-a-high-level-ag-grid-event-to-listen-to-any-change-to-column-state
     */
    const onColumnAnyChanged = (type, event) => {
      if (type.indexOf('column') >= 0) {
        emit('column-any-changed', event)
      }
    }

    /**
     * ag-grid の onGridReady イベントコールバック
     */
    const onGridReady = (params) => {
      gridApi.value = params.api

      // カラム系イベントのショートハンド登録
      params.api.addGlobalListener(onColumnAnyChanged)

      // イベント再送
      emit('grid-ready', params)
    }

    /**
     * デフォルト適用する ag-grid の props
     */
    const defaultProps = {
      rowSelection: 'multiple',
      suppressRowDeselection: true,
      suppressRowClickSelection: false,
      rowMultiSelectWithClick: true,
      suppressCellFocus: true,
      alwaysMultiSort: true,
      animateRows: false,
      suppressNoRowsOverlay: true,
      scrollbarWidth: 10,
    }

    /**
     * 実効カラム定義
     * 選択用チェックボックスカラムが先頭に追加される
     * @type {ComputedRef.<Object[]>}
     */
    const effectiveColumnDefs = computed(() => [
      {
        colId: '__select',
        headerName: '',
        resizable: false,
        sortable: false,
        lockPosition: 'left',
        pinned: 'left',
        lockPinned: true,
        maxWidth: 38,
        headerClass: 'header-check',
        cellClass: 'cell-check',
        headerComponent: 'BFormCheckboxHeaderRenderer',
        cellRenderer: 'BFormCheckboxCellRenderer',
      },
      ...props.columnDefs,
    ])

    /**
     * ---
     * 選択の v-model 化
     * ---
     */
    /**
     * 選択値(ローカル保持用)
     * @type {Ref.<number[]|string[]>}
     */
    const localValue = ref(props.value)
    // props 変更をローカル値に反映
    watch(
      () => props.value,
      (v) => {
        localValue.value = v
        applySelection()
      }
    )
    /**
     * 選択の変更を反映する
     */
    const applySelection = () => {
      if (!gridApi.value) return

      const values = localValue.value || []
      const valueSet = props.returnObject
        ? new Set(values.map((v) => v[props.valueField]))
        : new Set(values)

      const nodesToSelect = []
      const nodesToDeselect = []
      gridApi.value.forEachNode((n) => {
        // n.dataが存在しない場合があるためチェック
        if (!n.data) return
        // valueSetにIDが含まれているかチェック
        if (valueSet.has(n.data[props.valueField])) {
          nodesToSelect.push(n)
        } else {
          nodesToDeselect.push(n)
        }
      })
      gridApi.value.setNodesSelected({ nodes: nodesToSelect, newValue: true })
      gridApi.value.setNodesSelected({
        nodes: nodesToDeselect,
        newValue: false,
      })
    }
    /**
     * ag-grid の onSelectionChanged イベントコールバック
     */
    const onSelectionChanged = () => {
      const selectedRows = gridApi.value.getSelectedRows()
      if (props.returnObject) {
        localValue.value = selectedRows
      } else {
        localValue.value = selectedRows.map((r) => r[props.valueField])
      }
    }
    // ローカル値変更をイベント発行
    watch(localValue, (v) => emit('input', v))

    onBeforeUnmount(() => {
      gridApi.value?.removeGlobalListener(onColumnAnyChanged)
    })

    return {
      defaultProps,
      effectiveColumnDefs,
      onGridReady,
      onSelectionChanged,
    }
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/mixin' as mixin;

.data-grid {
  width: auto;
  max-width: 100%;
  height: 100%;

  --ag-foreground-color: #{$text-color};
  --ag-borders: 1px solid;
  --ag-border-color: #ebebeb;
  --ag-secondary-border-color: #ebebeb;
  --ag-cell-horizontal-border: 1px solid var(--ag-row-border-color);

  --ag-font-family: #{$font-family};
  --ag-font-size: #{$font-size-middle};

  --ag-header-background-color: #dddddd;
  --ag-odd-row-background-color: #f5f5f5;

  --ag-grid-size: 4px;
  --ag-header-height: 38px;
  --ag-row-height: 38px;
  --ag-cell-horizontal-padding: 16px;

  // リサイズハンドルの設定
  // 幅1pxだと範囲が狭いので幅を拡げるが、見た目に影響が無いように色は透明化する
  --ag-header-column-resize-handle-height: 100%;
  --ag-header-column-resize-handle-width: 2px;
  --ag-header-column-resize-handle-color: transparent;

  ::v-deep .ag-header-cell {
    border-right: var(--ag-borders) var(--ag-row-border-color);

    // ヘッダーのタイトルとソート表示の配置
    // 数値系カラムのヘッダー
    &.ag-right-aligned-header {
      .ag-header-cell-label {
        justify-content: flex-end;
        flex-direction: row;
      }
    }
    // 数値系以外のカラムのヘッダー
    &:not(.ag-right-aligned-header) {
      .ag-header-cell-label {
        justify-content: space-between;
      }
    }
  }

  // ソート表示部品
  ::v-deep .ag-sort-indicator-container {
    flex-direction: row-reverse;
    align-items: flex-end;

    .ag-icon {
      color: $text-color-link;
    }
    .ag-sort-order {
      color: $text-color-link;
      padding-left: 0;
    }
  }

  // スクロールバー
  ::v-deep .ag-body-viewport,
  ::v-deep .ag-center-cols-viewport {
    scrollbar-width: none;
  }
  ::v-deep .ag-body-vertical-scroll-viewport,
  ::v-deep .ag-body-horizontal-scroll-viewport {
    @include mixin.scrollbar(var(--ag-control-panel-background-color));
  }

  // 固定カラム部の水平スクロールバーのスペーサー
  ::v-deep .ag-horizontal-left-spacer {
    // ブラウザ標準のスクロールバーを出さないよう調整
    overflow: hidden;
    background-color: var(--ag-control-panel-background-color);
  }

  // ヘッダーのチェックボックスセル
  ::v-deep .header-check {
    padding: 0;
    .ag-header-cell-comp-wrapper {
      justify-content: center;
      overflow: visible;
    }
  }

  // 各行のチェックボックスセル
  ::v-deep .cell-check {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
}
</style>
